import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  affirmationPlanAdminService,
  AffirmationPlanAnalytics,
  AffirmationPlanListItem,
} from '../services/apiService';

const STATUS_TABS = [
  { key: '', label: 'Tüm Programlar' },
  { key: 'PendingApproval', label: 'Onay Bekleyenler' },
  { key: 'Approved', label: 'Onaylananlar' },
  { key: 'Active', label: 'Aktif Programlar' },
  { key: 'Completed', label: 'Tamamlananlar' },
  { key: 'Rejected', label: 'Reddedilenler' },
  { key: 'GenerationFailed', label: 'Üretim Hataları' },
];

const AdminAffirmationPlans: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('PendingApproval');
  const [rows, setRows] = useState<AffirmationPlanListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AffirmationPlanAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [listRes, analyticsRes] = await Promise.all([
          affirmationPlanAdminService.list(token, { status: status || undefined, pageSize: 100 }),
          affirmationPlanAdminService.analytics(token),
        ]);
        if (cancelled) return;
        setRows(Array.isArray(listRes.data) ? listRes.data : []);
        setAnalytics(analyticsRes.data ?? null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Liste yüklenemedi');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [token, status]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">21 Günlük Programlar</h1>
        <p className="mt-1 text-sm text-gray-600">Kişisel olumlama programı onay paneli</p>
      </div>

      {analytics && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {[
            ['Talep', analytics.totalRequests],
            ['Onay bekleyen', analytics.pendingApproval],
            ['Aktif', analytics.activePlans],
            ['Tamamlanan', analytics.completedPlans],
            ['Red', analytics.rejectedPlans],
            ['Üretim hatası', analytics.generationFailed],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-xl font-semibold text-gray-900">{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key || 'all'}
            type="button"
            onClick={() => setStatus(t.key)}
            className={`rounded-full px-4 py-2 text-sm ${
              status === t.key
                ? 'bg-purple-600 text-white'
                : 'border border-gray-100 bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-600">Yükleniyor…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-gray-100 bg-white p-8 text-center text-gray-500 shadow-sm">
          Bu filtrede program yok.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b bg-gray-50 text-gray-600">
              <tr>
                <th className="p-3">Kullanıcı</th>
                <th className="p-3">Konu</th>
                <th className="p-3">Paket</th>
                <th className="p-3">Durum</th>
                <th className="p-3">Gün</th>
                <th className="p-3">%</th>
                <th className="p-3">Talep</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-b hover:bg-purple-50"
                  onClick={() => navigate(`/admin/affirmation-plans/${r.id}`)}
                >
                  <td className="p-3">
                    <div className="font-medium text-gray-900">{r.userName || '—'}</div>
                    <div className="text-xs text-gray-500">{r.userEmail}</div>
                  </td>
                  <td className="p-3">
                    <div>{r.topic}</div>
                    <div className="line-clamp-1 text-xs text-gray-500">{r.goal}</div>
                  </td>
                  <td className="p-3">{r.isTrial ? 'Deneme' : r.packageName || '—'}</td>
                  <td className="p-3">{r.status}</td>
                  <td className="p-3">{r.currentDay}/21</td>
                  <td className="p-3">{Math.round(r.completionPercentage)}</td>
                  <td className="p-3">{new Date(r.requestedAt).toLocaleString('tr-TR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminAffirmationPlans;
